<template>
  <v-row>
    <v-col>
      <v-checkbox v-model="filtering" label="Filter Words" />
    </v-col>
    <v-col>
      <v-checkbox v-model="showStudentTopWords" @click="showTeacherTopWords = false" label="Show Student Top 10 Words" />
    </v-col>
    <v-col>
      <v-checkbox v-model="showTeacherTopWords" @click="showStudentTopWords = false" label="Show Teacher Top 10 Words" />
    </v-col>
  </v-row>
  <div class="chart-container">
    <bar-chart :data="chartData" :options="chartOptions" />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType, ref } from 'vue';
import { Bar } from 'vue-chartjs';
import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    ChartOptions,
    ChartData,
} from 'chart.js';
import { WordCount } from '@/models/models';

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

export default defineComponent({
    name: 'WordChart',
    components: {
        BarChart: Bar,
    },
    props: {
        wordCount: {
            type: Object as PropType<WordCount>,
            required: true,
        }
    },
    setup(props) {
        const filterWords = ['I', 'you', 'we', 'am', 'are', 'is', 'Uh', 'the', 'uh', 'a', 'Yeah', 'yeah', 'Uh-huh'];
        const filtering = ref(true);
        const showStudentTopWords = ref(false);
        const showTeacherTopWords = ref(false);

        const getTopWords = (wordCounts: {[key:string]: number}, showTopWords: boolean) => {
            const sortedWords = filtering.value ? Object.entries(wordCounts)
            .filter(([word]) => !filterWords.includes(word))
            .sort(([, a], [, b]) => b - a) :
            Object.entries(wordCounts)
            .sort(([, a], [, b]) => b - a)
            
            return showTopWords ? sortedWords.slice(0, 20) : sortedWords;
        };

        const teacherTopWords = computed(() => 
            getTopWords(props.wordCount.teacher_word_count, showTeacherTopWords.value)
        );
        const studentTopWords = computed(() => 
            getTopWords(props.wordCount.student_word_count, showStudentTopWords.value)
        );

        const allWords = computed(() => {
            if (showStudentTopWords.value) {
              const studentWords = studentTopWords.value.map(([word]) => word);
              return filtering.value
                ? studentWords.filter(word => !filterWords.includes(word))
                : studentWords
            } else if (showTeacherTopWords.value) {
              const teacherWords = teacherTopWords.value.map(([word]) => word);
              return filtering.value
                ? teacherWords.filter(word => !filterWords.includes(word))
                : teacherWords
            } else {
              const combinedWords = Array.from(new Set([
                ...teacherTopWords.value.map(([word]) => word),
                ...studentTopWords.value.map(([word]) => word),
              ]));
              return filtering.value
                    ? combinedWords.filter(word => !filterWords.includes(word))
                    : combinedWords;
            }
        });

        const teacherData = computed(() =>
            allWords.value.map((word: string) => 
                props.wordCount.teacher_word_count[word] || 0
            )
        );

        const studentData = computed(() =>
            allWords.value.map((word: string) => 
                props.wordCount.student_word_count[word] || 0
            )
        );

        const chartData = computed<ChartData<'bar'>>(() => ({
            labels: allWords.value,
            datasets: [
                {
                    label: 'Teacher Word Count',
                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                    borderColor: 'rgba(75, 192, 192, 1)',
                    borderWidth: 1,
                    data: teacherData.value,
                },
                {
                    label: 'Student Word Count',
                    backgroundColor: 'rgba(255, 99, 132, 0.2)',
                    borderColor: 'rgba(255, 99, 132, 1)',
                    borderWidth: 1,
                    data: studentData.value,
                },
            ],
        }));

        const chartOptions: ChartOptions<'bar'> = {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                y: {
                    beginAtZero: true,
                },
            },
        };

        return {
            filtering,
            showStudentTopWords,
            showTeacherTopWords,
            chartData,
            chartOptions,
        }
    },
});
</script>

<style scoped>
.chart-container {
    position: relative;
    width: 95%;
    height: 400px;
    margin: auto;
}
</style>
